import { ClockCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useMemo } from 'react';
import { TriangleIcon } from '../../../assets/CustomIcons/TriangleIcon';
import { sidebarWidth, sidebarWidthCollapsed } from '../../../constants/styles/mixins';
import { useLayoutContext } from '../../../pages/BaseIndex/context/LayoutContext';
import { useGetWorkspace } from '../../../serverState/workspace';
import { WorkspaceEstimationType } from '../../../types/Workspace';
import { TaskEstimateMapping } from '../../TaskTable/helpers/helper';
import { TaskTableDataType } from '../../TaskTable/types/TaskTableTypes';
import CustomTooltip from '../../UI/Tooltip/CustomTooltip';
import { SprintNameTitleInput } from '../Input/SprintNameTitleInput';
import { PlanningHeader } from '../PlanningHeader';

interface PlanningSessionHeaderProps {
  planningSessionId: number | undefined;
  nextSprintName: string | undefined;
  dataSource: TaskTableDataType[];
}

export const PlanningSessionHeader = ({
  planningSessionId,
  nextSprintName,
  dataSource,
}: PlanningSessionHeaderProps) => {
  const layoutContext = useLayoutContext();
  const workspaceQuery = useGetWorkspace();

  const planningStoryPoints = useMemo(() => {
    if (workspaceQuery.data?.estimationType === WorkspaceEstimationType.tshirtSizes) {
      const sizeDistribution = dataSource?.reduce(
        (acc, task) => {
          if (task.estimate) {
            const size = task.estimate;
            acc[size] = (acc[size] || 0) + 1;
          }
          return acc;
        },
        {} as Record<string, number>
      );
      return Object.entries(sizeDistribution)
        .sort((a, b) => Number(a[0]) - Number(b[0]))
        .map(([size, count]) => `${TaskEstimateMapping(WorkspaceEstimationType.tshirtSizes)[Number(size)]} (${count})`)
        .join(', ');
    } else {
      return dataSource?.reduce(
        (sum: number, task: TaskTableDataType) =>
          sum +
          (task.storyPoints ? Number(TaskEstimateMapping(WorkspaceEstimationType.storyPoints)[task.storyPoints]) : 0),
        0
      );
    }
  }, [dataSource, workspaceQuery.data?.estimationType]);

  // Check if there are any estimates to display
  const hasEstimates = useMemo(() => {
    if (workspaceQuery.data?.estimationType === WorkspaceEstimationType.tshirtSizes) {
      return typeof planningStoryPoints === 'string' && planningStoryPoints.length > 0;
    } else {
      return typeof planningStoryPoints === 'number' && planningStoryPoints > 0;
    }
  }, [planningStoryPoints, workspaceQuery.data?.estimationType]);

  return (
    <div className="flex flex-col">
      <div
        className="bg-blue-100 justify-start items-center gap-2 flex h-[52px] sticky left-0"
        style={{
          width: `calc(100vw - ${layoutContext?.collapsed ? sidebarWidthCollapsed : sidebarWidth}px)`,
        }}
      >
        <div className="flex justify-start items-center w-14"></div>
        <ClockCircleOutlined className="w-5 h-5 text-neutral-900" />
        <div className="text-brand-grey-dark text-xl font-semibold leading-7">Planning of the next Sprint:</div>
        <SprintNameTitleInput sprintName={nextSprintName || ''} planningSessionId={planningSessionId || undefined} />

        <div className="flex items-center gap-2 ml-auto mr-12">
          {dataSource.length === 0 && (
            <div className="flex items-center gap-2 bg-brand-white rounded-sm p-2">
              <InfoCircleOutlined className="text-neutral-900" />
              <div className="text-neutral-900 text-xs font-normal leading-none">
                Select items you will work on during the next Sprint.
              </div>
            </div>
          )}
          {dataSource?.length > 0 && (
            <div className="flex items-center gap-2">
              <CustomTooltip title="Total Tasks planned for next Sprint">
                <div className="px-2 py-1 bg-neutral-200 rounded text-neutral-900 text-xs font-semibold leading-tight cursor-default">
                  {dataSource?.length}
                </div>
              </CustomTooltip>
              {hasEstimates && (
                <CustomTooltip
                  title={
                    workspaceQuery.data?.estimationType === WorkspaceEstimationType.storyPoints
                      ? 'Total Story Points planned for next Sprint'
                      : ''
                  }
                >
                  <div className="flex items-center gap-1 pr-2 cursor-default">
                    <TriangleIcon className="text-neutral-900" />
                    <div className="justify-start text-grey-dark text-sm font-normal leading-none">
                      {planningStoryPoints}
                    </div>
                  </div>
                </CustomTooltip>
              )}
            </div>
          )}
          <PlanningHeader hasPlanningTasks={dataSource?.length > 0} />
        </div>
      </div>
    </div>
  );
};
